/**
 * ECP Correlation Inspector — Phase 2.B Step 8.B (Frontend Wiring)
 *
 * Two-tab view:
 *   • Correlation Tab — paste a correlation_id → see the ordered event chain
 *     (PPR / Reservation / Workflow / Stock etc.). Auto-loads when the URL
 *     contains ?correlation_id=...
 *   • DLQ Tab — failed events queue with discard action.
 *
 * Admin / debug-only.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Search, Clock, Hash, Layers, User as UserIcon, Activity,
  AlertTriangle, RefreshCw, Trash2, Loader2, Brain, ListFilter,
  ShieldAlert, X, Unlock, Play,
} from 'lucide-react';

const EVENT_ICONS = {
  // PPR lifecycle
  'ppr.created': '🟢',
  'ppr.submitted': '📤',
  'ppr.approved': '✅',
  'ppr.rejected': '❌',
  'ppr.cancelled': '🚫',
  'ppr.over_request_overridden': '⚠️',
  // Stock / Reservation (Step 4 + S2P)
  'reservation.created': '🔒',
  'reservation.released': '🔓',
  'reservation.expired_candidate': '⏰',
  'reservation.expiry_dismissed': '✋',
  'reservation.force_released': '💥',
  'stock.insufficient': '📉',
  'stock.fully_reserved': '📦',
  // MAR
  'mar.draft.created': '📄',
  'mar.submitted': '📤',
  'mar.qa.approved': '🛡️',
  'mar.consultant.approved': '🏛️',
  'mar.rejected': '❌',
  // MIR (Sprint 3 Step 3.1)
  'mir.draft.created': '📋',
  'mir.issued': '🚚',
  'mir.received': '📥',
  'mir.inspected': '🔬',
  'mir.accepted': '✔️',
  'mir.rejected': '⛔',
  'reservation.consumed_partial': '🧱',
  'reservation.consumed_full': '🔥',
  'reservation.consumption_failed': '⚠️',
  // Workflow
  'workflow.started': '🌀',
  'workflow.approved': '👍',
  'workflow.rejected': '👎',
  // Journal
  'JournalPosted': '📒',
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ECPInspector = () => {
  const [tab, setTab] = useState('correlation');
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <div data-testid="ecp-inspector" className="min-h-screen bg-gray-50">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
            <Brain className="w-4 h-4" /> Event Control Plane · Inspector
          </div>
          <h1 className="text-2xl font-bold text-gray-900">تتبّع سلاسل الأحداث</h1>
          <p className="text-sm text-gray-500 mt-1">
            استكشف correlation chains وأحداث Dead Letter Queue للنظام بأكمله (admin / debug).
          </p>
          <div className="flex gap-1 mt-5">
            <TabBtn
              id="correlation"
              active={tab}
              onClick={setTab}
              icon={<Activity className="w-4 h-4" />}
            >
              Correlation Chain
            </TabBtn>
            <TabBtn
              id="dlq"
              active={tab}
              onClick={setTab}
              icon={<AlertTriangle className="w-4 h-4" />}
            >
              Dead Letter Queue
            </TabBtn>
            <TabBtn
              id="stream"
              active={tab}
              onClick={setTab}
              icon={<ListFilter className="w-4 h-4" />}
            >
              Audit Stream
            </TabBtn>
            <TabBtn
              id="watchdog"
              active={tab}
              onClick={setTab}
              icon={<ShieldAlert className="w-4 h-4" />}
            >
              Watchdog
            </TabBtn>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {tab === 'correlation' && (
          <CorrelationTab
            searchParams={searchParams}
            setSearchParams={setSearchParams}
          />
        )}
        {tab === 'dlq' && <DLQTab />}
        {tab === 'stream' && <AuditStreamTab onJump={setTab} setSearchParams={setSearchParams} setTab={setTab} />}
        {tab === 'watchdog' && <WatchdogTab setSearchParams={setSearchParams} setTab={setTab} />}
      </div>
    </div>
  );
};

const TabBtn = ({ id, active, onClick, children, icon }) => (
  <button
    data-testid={`ecp-tab-${id}`}
    onClick={() => onClick(id)}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
      active === id
        ? 'border-indigo-600 text-indigo-700'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

// ─── Correlation Tab ──────────────────────────────────────────────────────

const CorrelationTab = ({ searchParams, setSearchParams }) => {
  const initial = searchParams.get('correlation_id') || '';
  const [correlationId, setCorrelationId] = useState(initial);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState(null);

  const lookup = async (cid) => {
    const id = (cid ?? correlationId).trim();
    if (!id) { toast.error('أدخل correlation_id'); return; }
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/ecp/audit/correlation/${id}`);
      setEvents(r.data.events || []);
      setMeta({ count: r.data.count, correlation_id: r.data.correlation_id });
      if ((r.data.events || []).length === 0) {
        toast.warning('لا توجد أحداث لهذا الـ correlation_id');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الاستعلام');
      setEvents([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-lookup when the URL ships with ?correlation_id=...
  useEffect(() => {
    if (!initial) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get(`/ecp/audit/correlation/${initial}`);
        if (cancelled) return;
        setEvents(r.data.events || []);
        setMeta({ count: r.data.count, correlation_id: r.data.correlation_id });
        if ((r.data.events || []).length === 0) {
          toast.warning('لا توجد أحداث لهذا الـ correlation_id');
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'فشل الاستعلام');
          setEvents([]);
          setMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initial]);

  const onSubmit = () => {
    setSearchParams(correlationId ? { correlation_id: correlationId } : {});
    lookup();
  };

  return (
    <div>
      {/* Search */}
      <div className="bg-white rounded-xl border p-4 mb-6 flex gap-2">
        <div className="flex-1 relative">
          <Hash className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="ecp-correlation-input"
            type="text"
            value={correlationId}
            onChange={(e) => setCorrelationId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
            placeholder="أدخل correlation_id (UUID من PPR / Reservation / Decision)"
            className="form-input w-full pl-10 font-mono text-sm border border-gray-300 rounded-lg py-2"
          />
        </div>
        <button
          data-testid="ecp-lookup-btn"
          onClick={onSubmit}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-5 py-2 rounded-md flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {loading ? 'جاري...' : 'بحث'}
        </button>
      </div>

      {/* Meta */}
      {meta && (
        <div
          data-testid="ecp-meta-card"
          className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 mb-4 flex items-center justify-between"
        >
          <div className="min-w-0">
            <div className="text-xs text-indigo-600 uppercase font-semibold">Correlation</div>
            <code className="text-sm text-indigo-900 font-mono truncate block">{meta.correlation_id}</code>
          </div>
          <div className="text-right ml-3">
            <div className="text-xs text-indigo-600 uppercase font-semibold">Events</div>
            <div className="text-2xl font-bold text-indigo-900">{meta.count}</div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {events.length > 0 && (
        <div className="space-y-3" data-testid="ecp-timeline">
          {events.map((e, idx) => (
            <EventCard
              key={e.event_id || idx}
              event={e}
              idx={idx}
              isLast={idx === events.length - 1}
            />
          ))}
        </div>
      )}

      {events.length === 0 && !loading && !meta && (
        <div className="text-center py-16 text-gray-500" data-testid="ecp-empty">
          <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>أدخل correlation_id لعرض timeline الأحداث</p>
          <p className="text-xs mt-2">
            يمكنك الحصول عليه من PRS Drawer، أو حقل <code>correlation_id</code> داخل أي PPR.
          </p>
        </div>
      )}
    </div>
  );
};

const EventCard = ({ event, idx, isLast }) => {
  const [expanded, setExpanded] = useState(false);
  const icon = EVENT_ICONS[event.name] || '⚪';
  const p = event.payload_snapshot || {};

  return (
    <div className="relative" data-testid={`ecp-event-${event.name}`}>
      {!isLast && <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-gray-200" />}
      <div className="bg-white border rounded-xl p-4 ml-12 relative">
        <div className="absolute -left-12 top-3 w-10 h-10 rounded-full bg-white border-2 border-indigo-200 flex items-center justify-center text-lg shadow-sm">
          {icon}
        </div>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <code className="text-sm font-bold text-indigo-700">{event.name}</code>
              <span className="text-xs text-gray-400">#{idx + 1}</span>
            </div>
            <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtTime(event.recorded_at)}
              </span>
              {p.actor_user_id && (
                <span className="flex items-center gap-1">
                  <UserIcon className="w-3 h-3" /> {String(p.actor_user_id).slice(0, 8)}…
                </span>
              )}
              {p.activity_code && (
                <span className="flex items-center gap-1">
                  <Layers className="w-3 h-3" /> {p.activity_code}
                </span>
              )}
              {p.ppr_no && (
                <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{p.ppr_no}</code>
              )}
              {p.request_type && (
                <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded uppercase">
                  {p.request_type}
                </span>
              )}
            </div>
            {Object.keys(p.extra || {}).length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                {Object.entries(p.extra).slice(0, 4).map(([k, v]) => (
                  <span
                    key={k}
                    className="inline-block bg-gray-50 border border-gray-200 px-2 py-0.5 rounded mr-1 mb-1"
                  >
                    <span className="text-gray-400">{k}:</span>{' '}
                    <code>{String(v).slice(0, 40)}</code>
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-indigo-600 hover:underline ml-3 shrink-0"
            data-testid={`ecp-expand-${idx}`}
          >
            {expanded ? 'إخفاء' : 'تفاصيل'}
          </button>
        </div>
        {expanded && (
          <pre
            data-testid={`ecp-event-payload-${idx}`}
            className="mt-3 bg-gray-900 text-green-200 text-[11px] p-3 rounded overflow-x-auto leading-relaxed"
          >
{JSON.stringify(p, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
};

const DLQTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(
        `/ecp/dlq${showResolved ? '' : '?resolved=false'}`
      );
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل DLQ');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showResolved]);

  const discard = async (eventId) => {
    if (!window.confirm('تأكيد تجاهل هذا الحدث الفاشل؟')) return;
    setActing(eventId);
    try {
      await axiosInstance.post(`/ecp/dlq/${eventId}/discard`);
      toast.success('تم تجاهل الحدث');
      await load();
    } catch (e) {
      toast.error('فشل التجاهل');
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="ecp-dlq-show-resolved"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          عرض المُحلولة
        </label>
        <button
          data-testid="ecp-dlq-refresh"
          onClick={load}
          className="ml-auto px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> تحديث
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500" data-testid="ecp-dlq-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="ecp-dlq-empty">
          <AlertTriangle className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
          <h3 className="font-bold text-gray-800">لا توجد أحداث فاشلة</h3>
          <p className="text-sm text-gray-500 mt-1">جميع الأحداث الحالية تم استهلاكها بنجاح ✨</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-3" data-testid="ecp-dlq-list">
          {rows.map((row) => (
            <DLQCard
              key={row.event_id}
              row={row}
              isActing={acting === row.event_id}
              onDiscard={() => discard(row.event_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DLQCard = ({ row, isActing, onDiscard }) => {
  const [expanded, setExpanded] = useState(false);
  const resolved = !!row.resolved;
  return (
    <div
      data-testid={`ecp-dlq-${row.event_id}`}
      className={`bg-white border rounded-xl p-4 ${resolved ? 'opacity-60' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">{EVENT_ICONS[row.name] || '⚠️'}</span>
            <code className="text-sm font-bold text-rose-700">{row.name}</code>
            {resolved && (
              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded uppercase">
                resolved
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
            <span><Clock className="w-3 h-3 inline" /> {fmtTime(row.moved_to_dlq_at)}</span>
            <span>retries: <strong>{row.retry_count}</strong></span>
            {row.correlation_id && (
              <a
                href={`/ecp?correlation_id=${row.correlation_id}`}
                className="text-indigo-600 hover:underline font-mono text-[10px]"
              >
                corr: {row.correlation_id.slice(0, 8)}…
              </a>
            )}
            {row.aggregate_id && (
              <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">
                agg: {row.aggregate_id.slice(0, 12)}…
              </code>
            )}
          </div>
          {row.last_error && (
            <div className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 font-mono break-all">
              {row.last_error.slice(0, 200)}{row.last_error.length > 200 ? '…' : ''}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-indigo-600 hover:underline"
            data-testid={`ecp-dlq-expand-${row.event_id}`}
          >
            {expanded ? 'إخفاء' : 'payload'}
          </button>
          {!resolved && (
            <button
              data-testid={`ecp-dlq-discard-${row.event_id}`}
              disabled={isActing}
              onClick={onDiscard}
              className="px-2 py-1 text-xs bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded flex items-center gap-1"
            >
              {isActing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              تجاهل
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <pre className="mt-3 bg-gray-900 text-green-200 text-[11px] p-3 rounded overflow-x-auto leading-relaxed">
{JSON.stringify(row.payload || {}, null, 2)}
        </pre>
      )}
    </div>
  );
};

// ─── Audit Stream Tab (Step 8.B.1) ─────────────────────────────────────────

const AuditStreamTab = ({ setSearchParams, setTab }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    name: '', project_id: '', correlation_id: '', since: '', until: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      params.append('limit', '200');
      const r = await axiosInstance.get(`/ecp/audit?${params.toString()}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل audit stream');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const jumpToCorrelation = (cid) => {
    if (!cid) return;
    setSearchParams({ correlation_id: cid });
    setTab('correlation');
  };

  const set = (k, v) => setFilters({ ...filters, [k]: v });

  return (
    <div data-testid="ecp-stream-tab">
      <div className="bg-white border rounded-xl p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <input
            data-testid="stream-filter-name"
            placeholder="Event name (e.g. ppr.created)"
            value={filters.name}
            onChange={(e) => set('name', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm font-mono"
          />
          <input
            data-testid="stream-filter-project"
            placeholder="Project ID"
            value={filters.project_id}
            onChange={(e) => set('project_id', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm font-mono"
          />
          <input
            data-testid="stream-filter-correlation"
            placeholder="Correlation ID"
            value={filters.correlation_id}
            onChange={(e) => set('correlation_id', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm font-mono"
          />
          <input
            data-testid="stream-filter-since"
            type="date"
            value={filters.since}
            onChange={(e) => set('since', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          />
          <input
            data-testid="stream-filter-until"
            type="date"
            value={filters.until}
            onChange={(e) => set('until', e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          />
          <button
            data-testid="stream-apply-btn"
            onClick={load}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            تطبيق الفلاتر
          </button>
        </div>
        <div className="text-xs text-gray-500">
          آخر <strong>{rows.length}</strong> حدث · انقر على أي صف لفتح correlation chain.
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500" data-testid="stream-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center text-gray-500" data-testid="stream-empty">
          لا توجد أحداث مطابقة. جرّب توسيع الفلاتر.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="stream-list">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs uppercase text-gray-600">
              <tr>
                <th className="px-3 py-2 text-right">الوقت</th>
                <th className="px-3 py-2 text-right">Event</th>
                <th className="px-3 py-2 text-right">Aggregate</th>
                <th className="px-3 py-2 text-right">Project</th>
                <th className="px-3 py-2 text-right">Correlation</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r, idx) => (
                <tr
                  key={r.event_id || idx}
                  data-testid={`stream-row-${idx}`}
                  onClick={() => jumpToCorrelation(r.correlation_id)}
                  className="hover:bg-indigo-50 cursor-pointer"
                >
                  <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                    {fmtTime(r.recorded_at)}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-lg mr-1">{EVENT_ICONS[r.name] || '⚪'}</span>
                    <code className="text-xs font-bold text-indigo-700">{r.name}</code>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.aggregate_type && (
                      <span className="bg-gray-100 px-2 py-0.5 rounded">{r.aggregate_type}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-gray-600">
                    {r.project_id ? `${r.project_id.slice(0, 8)}…` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs font-mono text-indigo-600">
                    {r.correlation_id ? `${r.correlation_id.slice(0, 8)}…` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// ─── Watchdog Tab (Step 4.1 — Ghost Reservations) ─────────────────────────

const FLAG_META = {
  candidate:        { label: 'Candidate',       cls: 'bg-amber-50 text-amber-700 border-amber-300', icon: '⏰' },
  dismissed:        { label: 'Dismissed',       cls: 'bg-gray-50 text-gray-700 border-gray-300',    icon: '✋' },
  force_released:   { label: 'Force-Released',  cls: 'bg-rose-50 text-rose-700 border-rose-300',     icon: '💥' },
};

const REASON_LABEL = {
  AGE_THRESHOLD:    'تجاوز عتبة العمر',
  NO_MIR_LINK:      'لا يوجد MIR مرتبط',
  NO_CONSUMPTION:   'لا يوجد استهلاك مسجل',
  MANUAL_REVIEW:    'مراجعة يدوية',
};

const ageDays = (isoOrNull) => {
  if (!isoOrNull) return null;
  try {
    const ms = Date.now() - new Date(isoOrNull).getTime();
    return Math.max(0, Math.floor(ms / 86_400_000));
  } catch { return null; }
};

const WatchdogTab = ({ setSearchParams, setTab }) => {
  const [rows, setRows] = useState([]);
  const [flagFilter, setFlagFilter] = useState('candidate');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [actingOn, setActingOn] = useState(null);
  const [modal, setModal] = useState(null); // { kind: 'dismiss' | 'force', row }
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const r = await axiosInstance.get(
          `/pece/reservations/expired-candidates?flag=${flagFilter}`,
        );
        if (!cancelled) setRows(r.data);
      } catch (e) {
        if (!cancelled) toast.error('فشل تحميل قائمة Watchdog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [flagFilter, reloadTick]);

  const reload = () => setReloadTick((t) => t + 1);

  const runScan = async () => {
    setRunning(true);
    try {
      const r = await axiosInstance.post('/pece/reservations/watchdog/run', {});
      const s = r.data;
      toast.success(
        `تم المسح · مرشحون جدد: ${s.candidates_found} · أحداث: ${s.events_emitted}`,
      );
      reload();
    } catch (e) {
      toast.error('فشل تشغيل Watchdog');
    } finally {
      setRunning(false);
    }
  };

  const submit = async () => {
    if (!modal) return;
    const { kind, row } = modal;
    setActingOn(row.id);
    try {
      if (kind === 'dismiss') {
        await axiosInstance.post(
          `/pece/reservations/${row.id}/dismiss-expiry`,
          { notes: modal.notes || null },
        );
        toast.success('تم تجاهل العلامة');
      } else {
        await axiosInstance.post(
          `/pece/reservations/${row.id}/force-release`,
          { reason: modal.reason, notes: modal.notes || null },
        );
        toast.success('تم التحرير القسري');
      }
      setModal(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الإجراء');
    } finally {
      setActingOn(null);
    }
  };

  const jumpToCorrelation = (correlation_id) => {
    if (!correlation_id) return;
    setSearchParams({ correlation_id });
    setTab('correlation');
  };

  return (
    <div data-testid="ecp-watchdog-tab">
      {/* Header strip */}
      <div className="bg-white rounded-xl border p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs text-gray-500 uppercase font-semibold">Ghost Reservation Watchdog</div>
          <div className="text-sm text-gray-700">
            مراقبة الحجوزات الراكدة لمنع تضخم PRS وTrust Score. الإجراءات يدوية بالكامل في v1.
          </div>
        </div>
        <select
          data-testid="watchdog-flag-filter"
          value={flagFilter}
          onChange={(e) => setFlagFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
        >
          {Object.entries(FLAG_META).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        <button
          data-testid="watchdog-run-btn"
          onClick={runScan}
          disabled={running}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white rounded-lg flex items-center gap-2 text-sm font-semibold"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          تشغيل المسح
        </button>
        <button
          data-testid="watchdog-refresh-btn"
          onClick={reload}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" /> تحديث
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500" data-testid="watchdog-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="watchdog-empty">
          <ShieldAlert className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h3 className="text-lg font-bold text-gray-800">لا توجد حجوزات في الحالة المحددة</h3>
          <p className="text-sm text-gray-500 mt-1">
            شغّل المسح أعلاه أو غيّر فلتر الحالة. النسخة v1 محافظة — لن يتم تحرير أي حجز تلقائياً.
          </p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="watchdog-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase text-gray-600 font-bold">
              <tr>
                <th className="px-3 py-2 text-right">المادة</th>
                <th className="px-3 py-2 text-right">الكمية</th>
                <th className="px-3 py-2 text-right">العمر</th>
                <th className="px-3 py-2 text-right">السبب</th>
                <th className="px-3 py-2 text-right">المشروع / النشاط</th>
                <th className="px-3 py-2 text-right">PPR</th>
                <th className="px-3 py-2 text-right">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => {
                const meta = FLAG_META[r.expiry_flag] || FLAG_META.candidate;
                const age = ageDays(r.reserved_at);
                return (
                  <tr key={r.id} className="hover:bg-amber-50/30" data-testid={`watchdog-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-gray-800">{r.material_name || '—'}</div>
                      <div className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border ${meta.cls}`}>
                        {meta.icon} {meta.label}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono">{r.qty}</td>
                    <td className="px-3 py-2">
                      <span className={`font-bold ${age && age >= 60 ? 'text-rose-700' : age && age >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>
                        {age != null ? `${age} يوم` : '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.flagged_reason_code ? (
                        <div>
                          <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{r.flagged_reason_code}</code>
                          <div className="text-gray-500 mt-0.5">{REASON_LABEL[r.flagged_reason_code] || r.flagged_reason_text}</div>
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-mono text-gray-600 truncate max-w-[140px]">{r.project_id?.slice(0, 8) || '—'}…</div>
                      {r.activity_code && <div className="font-mono text-gray-500">{r.activity_code}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {r.correlation_id ? (
                        <button
                          data-testid={`watchdog-correlation-${r.id}`}
                          onClick={() => jumpToCorrelation(r.correlation_id)}
                          className="text-indigo-600 underline hover:no-underline text-xs"
                        >
                          {r.request_id ? r.request_id.slice(0, 8) : 'سلسلة الحدث'}…
                        </button>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {r.expiry_flag === 'candidate' ? (
                        <div className="flex gap-1">
                          <button
                            data-testid={`watchdog-dismiss-${r.id}`}
                            disabled={actingOn === r.id}
                            onClick={() => setModal({ kind: 'dismiss', row: r, notes: '' })}
                            className="px-2 py-1.5 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50 flex items-center gap-1"
                          >
                            <X className="w-3.5 h-3.5" /> تجاهل
                          </button>
                          <button
                            data-testid={`watchdog-force-release-${r.id}`}
                            disabled={actingOn === r.id}
                            onClick={() => setModal({ kind: 'force', row: r, reason: '', notes: '' })}
                            className="px-2 py-1.5 bg-rose-600 text-white text-xs rounded hover:bg-rose-700 flex items-center gap-1"
                          >
                            <Unlock className="w-3.5 h-3.5" /> تحرير
                          </button>
                        </div>
                      ) : (
                        <div className="text-[10px] text-gray-500">
                          {r.dismissed_by && <div>بواسطة: {r.dismissed_by?.slice(0, 8)}…</div>}
                          {r.force_released_by && <div>بواسطة: {r.force_released_by?.slice(0, 8)}…</div>}
                          {r.force_release_reason && <div>سبب: {r.force_release_reason}</div>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ActionModal
          modal={modal}
          setModal={setModal}
          onConfirm={submit}
          isActing={actingOn === modal.row.id}
        />
      )}
    </div>
  );
};

const ActionModal = ({ modal, setModal, onConfirm, isActing }) => {
  const isForce = modal.kind === 'force';
  return (
    <div
      data-testid="watchdog-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={() => setModal(null)}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-1">
          {isForce ? 'تحرير قسري للحجز' : 'تجاهل علامة الانتهاء'}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          المادة: <span className="font-semibold">{modal.row.material_name}</span> ·
          الكمية: <span className="font-mono">{modal.row.qty}</span>
        </p>
        {isForce && (
          <div className="mb-3">
            <label className="text-xs font-bold uppercase text-gray-600">السبب (إجباري)</label>
            <input
              data-testid="watchdog-modal-reason"
              type="text"
              value={modal.reason || ''}
              onChange={(e) => setModal({ ...modal, reason: e.target.value })}
              placeholder="مثال: supplier_bankruptcy, project_paused, reallocate..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-1"
            />
          </div>
        )}
        <textarea
          data-testid="watchdog-modal-notes"
          placeholder="ملاحظات إضافية (اختياري)..."
          value={modal.notes || ''}
          onChange={(e) => setModal({ ...modal, notes: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4 h-24 resize-none"
        />
        <div className="flex justify-end gap-2">
          <button
            data-testid="watchdog-modal-cancel"
            onClick={() => setModal(null)}
            disabled={isActing}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
          >
            إلغاء
          </button>
          <button
            data-testid="watchdog-modal-confirm"
            onClick={onConfirm}
            disabled={isActing || (isForce && !modal.reason?.trim())}
            className={`px-4 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1 ${
              isForce ? 'bg-rose-600 hover:bg-rose-700' : 'bg-gray-700 hover:bg-gray-800'
            }`}
          >
            {isActing ? <Loader2 className="w-4 h-4 animate-spin" /> :
              isForce ? <Unlock className="w-4 h-4" /> : <X className="w-4 h-4" />}
            {isForce ? 'تأكيد التحرير' : 'تأكيد التجاهل'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ECPInspector;
